import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const IDLE_MS = 5 * 60 * 1000; // 5 minutos

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* noop */ }
    localStorage.removeItem("dr_token");
    setUser(null);
  }, []);

  const applyAuth = (data) => {
    if (data.session_token) localStorage.setItem("dr_token", data.session_token);
    setUser(data.user);
    return data.user;
  };

  const loginWithPassword = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    return applyAuth(data);
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    return applyAuth(data);
  };

  // Logout automático por inatividade (> 5 min) quando logado
  useEffect(() => {
    if (!user) return undefined;
    let timer;
    const trigger = async () => {
      await logout();
      window.location.href = "/login?idle=1";
    };
    const reset = () => { clearTimeout(timer); timer = setTimeout(trigger, IDLE_MS); };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => { clearTimeout(timer); events.forEach((e) => window.removeEventListener(e, reset)); };
  }, [user, logout]);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, refresh, logout, loginWithPassword, register }}>
      {children}
    </AuthContext.Provider>
  );
}
